import { useEffect, useMemo } from "react";
import { Pencil } from "lucide-react";
import { get, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import { Button } from "@/components/ui/button";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import { DeliveryContentLineProfileControlsProps } from "@/components/backOffice/expeditionForm/expeditionForm.types";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { ProductDto, ProductUSA } from "@/types/api/api-types";

export const DeliveryContentLineProfileControls = ({
  index,
  products,
  isUsaProducts,
  onChooseProfile,
  onCreateProfile,
  onEditProfile,
  destinationRegion,
  viaImportedInvoice,
}: DeliveryContentLineProfileControlsProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const translateMessage = useTranslateMessage();
  const { control, setValue } = useFormContext<QuotationUpdateBoFormData>();
  const profileField = isUsaProducts ? "productUSAId" : "productId";
  const { errors } = useFormState({
    control,
    name: `deliveryContent.${index}.${profileField}`,
  });
  const productId = useWatch({ control, name: `deliveryContent.${index}.productId` });
  const productUSAId = useWatch({ control, name: `deliveryContent.${index}.productUSAId` });
  const assignedId = isUsaProducts ? productUSAId : productId;
  const resolvedProduct: ProductDto | ProductUSA | undefined = useMemo(
    () => products?.find((p) => p.id === assignedId),
    [products, assignedId],
  );
  const profileErrorMessage = get(errors, `deliveryContent.${index}.${profileField}.message`) as
    | string
    | undefined;

  const clearProfile = () => {
    setValue(`deliveryContent.${index}.productId`, undefined, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue(`deliveryContent.${index}.productUSAId`, undefined, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  useEffect(() => {
    if (!viaImportedInvoice) return;

    if (!resolvedProduct) {
      if (destinationRegion === "UE") {
        setValue(`deliveryContent.${index}.wineName`, undefined as unknown as string, {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue(`deliveryContent.${index}.alcoholPercent`, undefined as unknown as number, {
          shouldDirty: true,
          shouldValidate: false,
        });
        // setValue(`deliveryContent.${index}.color`, undefined as unknown as WineColorEnum, {
        //   shouldDirty: true,
        //   shouldValidate: false,
        // });
      } else if (destinationRegion === "USA") {
        setValue(`deliveryContent.${index}.wineName`, undefined as unknown as string, {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue(`deliveryContent.${index}.alcoholPercent`, undefined as unknown as number, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
      return;
    }

    if (destinationRegion === "UE") {
      setValue(`deliveryContent.${index}.wineName`, resolvedProduct.description, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.year`, "-", {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.alcoholPercent`, 0, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.color`, resolvedProduct.color as WineColorEnum, {
        shouldDirty: true,
        shouldValidate: false,
      });
    } else if (destinationRegion === "USA" && isUsaProducts) {
      const usaProduct = resolvedProduct as ProductUSA;
      setValue(`deliveryContent.${index}.wineName`, usaProduct.description, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.color`, usaProduct.color as WineColorEnum, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.year`, "-", {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue(`deliveryContent.${index}.alcoholPercent`, usaProduct.alcoholPercentage, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProduct]);

  if (!assignedId || !resolvedProduct) {
    return (
      <div className="flex flex-row flex-wrap items-center gap-2">
        <Button
          className="text-blue-600"
          variant="ghost"
          type="button"
          onClick={() => onChooseProfile(index)}
        >
          {t("deliveryContentProfile.chooseProfile")}
        </Button>
        <Button
          className="text-blue-600"
          variant="ghost"
          type="button"
          onClick={() => onCreateProfile(index)}
        >
          {t("deliveryContentProfile.createProfile")}
        </Button>
        {profileErrorMessage && (
          <p className="w-full text-sm text-red-500">{translateMessage(profileErrorMessage)}</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-wrap items-center gap-2">
      <span className="text-sm text-gray-800">
        {resolvedProduct.description} - {resolvedProduct.harmonizedCode}
      </span>
      <Button type="button" variant="outline" size="sm" onClick={clearProfile}>
        {t("deliveryContentProfile.removeProfile")}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        aria-label={t("deliveryContentProfile.editProfile")}
        onClick={() => onEditProfile(index, resolvedProduct)}
      >
        <Pencil className="h-4 w-4" />
      </Button>
    </div>
  );
};
